import type { HTMLAttributes } from 'react'

import { Button } from '../../actions/button/Button'
import { useTranslate } from '../../hooks/useTranslate'
import { Modal } from '../modal/Modal'

type Props = {
  /**
   * State of the confirmation box
   */
  isOpen: boolean
  /**
   * Action to close to modal
   */
  onCancel: () => void
  /**
   * The title of the modal.
   */
  title: string
  /**
   * Action that will be executed when the user clicks the confirm button.
   */
  onAccept: () => void | Promise<void>
  /**
   * @defaultValue `true`
   * Shown at the foot next to the action button. This is by default shown
   */
  isCancelVisible?: boolean
  /**
   * @defaultValue `translate('cancel')`
   * Label to use for the "Cancel" button.
   */
  cancelLabel?: string
  /**
   * @defaultValue `translate('confirm')`
   * Label to use for the "Confirm" button.
   */
  confirmLabel?: string
  /**
   * Loading state shown on the button
   */
  isLoading?: boolean
  isButtonDisabled?: boolean
  isDanger?: boolean
  panelClassName?: string
} & HTMLAttributes<HTMLElement>

/**
 * Used to display a confirmation modal. To make sure the user is aware of the consequences of the action,
 */
export const ConfirmationBox = ({
  isOpen,
  onCancel,
  title,
  onAccept,
  isCancelVisible = true,
  cancelLabel,
  confirmLabel,
  children,
  isLoading,
  isButtonDisabled = false,
  isDanger,
  panelClassName,
  ...htmlProps
}: Props) => {
  const translate = useTranslate()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      panelClassName={panelClassName}
      {...htmlProps}
    >
      <Modal.Title title={title} />
      <Modal.Body>{children}</Modal.Body>
      <Modal.Footer>
        {isCancelVisible && (
          <Button variant="discreet" onClick={onCancel}>
            {cancelLabel ?? translate('cancel')}
          </Button>
        )}
        <Button
          variant="primary"
          //Closing the modal should be done on the outside of the modal
          onClick={async () => {
            await onAccept()
          }}
          isLoading={isLoading}
          disabled={isButtonDisabled}
          isDanger={isDanger}
        >
          {confirmLabel ?? translate('confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
